import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ArrowRight, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../mock';

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const slides = mockData.slides;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => {
        setPrevSlide(prev);
        return (prev + 1) % slides.length;
      });
    }, 6000);
  }, [clearTimer, slides.length]);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  const resetTimer = useCallback(() => {
    startTimer();
  }, [startTimer]);

  const goToSlide = useCallback(
    (index) => {
      setPrevSlide((prev) => (prev === index ? prev : currentSlide));
      setCurrentSlide(index);
      resetTimer();
    },
    [currentSlide, resetTimer]
  );

  const navigateToPage = (page) => {
    if (page.startsWith('/')) {
      navigate(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(page);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const slidesToRender = useMemo(() => {
    const indices = [prevSlide, currentSlide].filter((i) => i !== null);
    const unique = [...new Set(indices)];
    return unique.map((index) => ({
      slide: slides[index],
      isCurrent: index === currentSlide,
    }));
  }, [slides, currentSlide, prevSlide]);

  return (
    <section id="home" className="relative h-screen overflow-hidden">
      {/* Slides */}
      {slidesToRender.map(({ slide, isCurrent }) => (
        <div
          key={`${slide.id}-${isCurrent ? 'current' : 'prev'}`}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            isCurrent ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
          }`}
          aria-hidden={!isCurrent}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slide.image})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
          </div>

          {/* Content */}
          <div className="relative h-full flex items-center">
            <div className="container mx-auto px-4 lg:px-8">
              <div className="max-w-3xl">
                {/* Badge */}
                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/20 dark:bg-white/10 backdrop-blur-sm border border-white/30 rounded-full mb-6 animate-fade-in">
                  <Zap className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white">Smart Living Innovation</span>
                </div>

                <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight">
                  {slide.title}
                </h1>
                <p className="text-2xl md:text-3xl text-white/95 mb-3 font-medium">
                  {slide.subtitle}
                </p>
                <p className="text-lg text-white/85 mb-6 max-w-xl">
                  {slide.description}
                </p>

                {/* Benefits */}
                <div className="space-y-2 mb-8">
                  {slide.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <span className="text-white/90">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Button
                    size="lg"
                    className="group px-8 py-6 text-lg font-semibold bg-white hover:bg-white/90 text-black transition-all duration-300 shadow-lg hover:shadow-xl"
                    onClick={() => navigateToPage(slide.page)}
                    data-testid="hero-cta-button"
                  >
                    {slide.cta}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="px-8 py-6 text-lg font-semibold border-2 border-white text-white hover:bg-white hover:text-black transition-all duration-300"
                    onClick={() => navigateToPage('/products')}
                    data-testid="hero-view-products-button"
                  >
                    View Products
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Dots Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-3 rounded-full transition-all ${
              index === currentSlide ? 'bg-white w-10' : 'bg-white/50 w-3'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

    </section>
  );
};

export default HeroCarousel;
